import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import MobileKpiRail from "@/components/MobileKpiRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Play } from "lucide-react";
import { toast } from "sonner";

export default function Payroll() {
  const { fmtMoney } = useCurrency();
  const now = new Date();
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [entries, setEntries] = useState([]);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/payroll", { params: { period } }).then((r) => setEntries(r.data || []));
  useEffect(() => { load(); }, [period]);

  const runPayroll = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/payroll/run?period=${period}`);
      toast.success(`Generated ${r.data.count} payroll entries`);
      load();
    } catch (e) {
      toast.error("Failed to run payroll");
    } finally {
      setRunning(false);
    }
  };

  const pay = async (id) => {
    await api.post(`/payroll/${id}/pay`);
    toast.success("Marked as paid");
    load();
  };

  const totals = {
    gross: entries.reduce((s, e) => s + e.gross, 0),
    tax: entries.reduce((s, e) => s + e.tax, 0),
    net: entries.reduce((s, e) => s + e.net, 0),
  };

  return (
    <div data-testid="payroll-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Payroll"
        subtitle={`Run and track staff salary for ${period}`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <Input data-testid="period-input" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM" className="w-32 rounded-full bg-white" />
            <Button data-testid="run-payroll-btn" onClick={runPayroll} disabled={running} className="rounded-full bg-black text-white hover:bg-black/85">
              <Play className="w-4 h-4 mr-1.5" /> {running ? "Running…" : "Run payroll"}
            </Button>
          </div>
        }
      />

      <MobileKpiRail columns={3} className="mb-6">
        <Card testid="payroll-gross"><div className="text-[11px] uppercase tracking-wider text-[#8E8E93]">Gross</div><div className="mt-2 font-display font-semibold text-[26px]">{entries.length ? fmtMoney(totals.gross) : "—"}</div></Card>
        <Card testid="payroll-tax"><div className="text-[11px] uppercase tracking-wider text-[#8E8E93]">Tax withheld</div><div className="mt-2 font-display font-semibold text-[26px] text-[#FF9500]">{entries.length ? fmtMoney(totals.tax) : "—"}</div></Card>
        <Card testid="payroll-net"><div className="text-[11px] uppercase tracking-wider text-[#8E8E93]">Net payout</div><div className="mt-2 font-display font-semibold text-[26px] text-[#34C759]">{entries.length ? fmtMoney(totals.net) : "—"}</div></Card>
      </MobileKpiRail>

      <Card>
        {entries.length === 0 ? (
          <div className="py-8 text-center text-[#8E8E93]">
            No payroll entries for {period}. Click &ldquo;Run payroll&rdquo; to generate.
          </div>
        ) : (
          <div className="data-table-scroll overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
          <table className="w-full text-[13px] min-w-[600px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#8E8E93]">
                <th className="py-3 px-3 font-medium">Staff</th>
                <th className="py-3 px-3 font-medium text-right">Gross</th>
                <th className="py-3 px-3 font-medium text-right">Tax (22%)</th>
                <th className="py-3 px-3 font-medium text-right">Net</th>
                <th className="py-3 px-3 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} data-testid={`payroll-row-${e.id}`} className="border-t border-black/[0.04]">
                  <td className="py-3 px-3 font-medium">{e.staff_name}</td>
                  <td className="py-3 px-3 text-right font-mono">{fmtMoney(e.gross)}</td>
                  <td className="py-3 px-3 text-right font-mono text-[#FF9500]">{fmtMoney(e.tax)}</td>
                  <td className="py-3 px-3 text-right font-mono font-semibold">{fmtMoney(e.net)}</td>
                  <td className="py-3 px-3 text-right">
                    {e.paid ? (
                      <span className="inline-flex items-center gap-1 text-[#34C759] text-[12px] font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Paid
                      </span>
                    ) : (
                      <Button data-testid={`pay-btn-${e.id}`} size="sm" variant="outline" onClick={() => pay(e.id)} className="rounded-full h-7 text-[11px]">
                        Mark paid
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}
